import React, { Component } from 'react';
import { compose } from 'recompose';
import { CardContent } from '@material-ui/core';
import { createStyles, WithStyles, withStyles } from '@material-ui/core/styles';

import {
  withContent,
  withUser,
  withQueryParams,
  withApolloClient,
  IWithContent,
  IWithUser,
  IWithApolloClient,
  IWithQueryParams,
} from 'providers';
import { EditorModes, EditorFormat, IEditorProps } from 'components';
import { Comment, CommentsList, Content, GetActiveCommentsDocument, PublicUserProfile } from 'api';
import { contentModel } from 'apollo/reducers';

import { Comments } from '../widgets';

interface IPropsEnhanced
  extends IWithUser,
    IWithContent,
    IWithApolloClient,
    IWithQueryParams,
    WithStyles<typeof styles> {}

interface IState {
  replyId: string;
  loading: boolean;
  editorProps: IEditorProps;
}

type NeedsLoadMore = (comments: Content['comments']) => boolean;
type LoadMoreLength = (comments: Content['comments']) => number;

const styles = () =>
  createStyles({
    cardContent: {
      paddingTop: 12,
    },
  });

const enhancers = compose<IPropsEnhanced, {}>(
  withApolloClient,
  withQueryParams,
  withContent,
  withUser,
  withStyles(styles),
);

class Footer extends Component<IPropsEnhanced, IState> {
  state: IState = {
    replyId: null,
    loading: false,
    editorProps: {
      format: EditorFormat.text,
      text: '',
      mentions: null,
      parentId: null,
      mode: EditorModes.create,
    },
  };

  getCommentList() {
    const { content } = this.props;
    const { comments } = content;

    return (
      comments ||
      ({
        comments: [],
        count: 0,
      } as CommentsList)
    );
  }

  needsLoadMore: NeedsLoadMore = comments => {
    return comments.count > comments.comments.length;
  };

  loadMoreLength: LoadMoreLength = comments => {
    return comments.count - comments.comments.length;
  };

  cancelReply = () => {
    this.setState({
      replyId: null,
      loading: false,
      editorProps: {
        format: EditorFormat.text,
        text: '',
        mentions: null,
        parentId: null,
        mode: EditorModes.create,
      },
    });
  };

  handleLoadMore = async () => {
    const { apolloClient, content, query, variables } = this.props;
    const { id, created_at } = this.getCommentList();

    this.setState({ loading: true });

    const result = await apolloClient.query({
      query: GetActiveCommentsDocument,
      fetchPolicy: 'no-cache',
      variables: {
        id,
        created_at,
        object_id: content.content_id,
      },
    });

    contentModel({
      cache: apolloClient.cache,
      contentId: content.content_id,
      query,
      variables,
      result,
    });

    this.setState({ loading: false });
  };

  onReply = (comment: Comment) => {
    const currentUser = this.props.user;
    const { replyId } = this.state;
    const { has_substitutions, user, users_mentioned, id } = comment;

    if (replyId && replyId === id) {
      return this.cancelReply();
    }

    const replyToSelf: boolean = currentUser.id === user.id;
    const mentionText: string[] = [];
    const mentions: PublicUserProfile[] = [];

    if (!replyToSelf) {
      mentionText.push(`<#U${user.id}>`);
      mentions.push(user);
    }

    if (has_substitutions) {
      users_mentioned.forEach(mentionedUser => {
        if (mentionedUser.id !== currentUser.id) {
          mentionText.push(`<#U${mentionedUser.id}>`);
          mentions.push(mentionedUser);
        }
      });
    }

    const editorText = mentionText.length ? `${mentionText.join(' ')} ` : '';

    this.setState({
      replyId: id,
      editorProps: {
        mode: EditorModes.create,
        format: EditorFormat.text,
        text: editorText,
        mentions,
        parentId: id,
      },
    });
  };

  render() {
    const { classes, content } = this.props;
    const { editorProps, loading, replyId } = this.state;

    const commentsObject = this.getCommentList();
    const { content_id } = content;
    const { comments } = commentsObject;

    return (
      <CardContent className={classes.cardContent}>
        {this.needsLoadMore(commentsObject) && (
          <Comments.LoadMore
            loading={loading}
            onLoadMore={this.handleLoadMore}
            length={this.loadMoreLength(commentsObject)}
          />
        )}
        <div>
          {comments.map(comment => (
            <Comments.Comment
              key={comment.id}
              comment={comment}
              onReply={() => this.onReply(comment)}
              isReplying={replyId === comment.id}
            />
          ))}
        </div>
        <Comments.Add
          onPosted={this.cancelReply}
          contentId={content_id}
          editorProps={editorProps}
        />
      </CardContent>
    );
  }
}

export default enhancers(Footer);
